import type { DriverActivity } from "@ots-frontend/ots-data";

/** A rate model revenue and cost can come from, and whether it bills hauling. */
export type ActivityRateModelOption = { name: string; hauling: boolean };

/** Hauling: a transport rate model billed to a truck company, or to no company in particular (WaterGo's rule). */
export const isHaulingRateModel = (type: string | undefined, companyType: string | undefined) =>
  type === "Transport" && (!companyType || companyType === "Truck Company");

/** The rate models on the day's tickets. */
export const dayRateModelOptions = (rows: DriverActivity[] | undefined): ActivityRateModelOption[] =>
  mergeRateModelOptions((rows ?? []).flatMap((row) => row.transportTransportAssoc?.objectCostRateModelList ?? [])
    .flatMap((model) => model.rateModelName ? [{ name: model.rateModelName, hauling: isHaulingRateModel(model.rateModelType, model.rateModelCompanyType) }] : []));

/** One option per name, sorted; a rate model any list says is hauling is hauling. */
export const mergeRateModelOptions = (...lists: (ActivityRateModelOption[] | undefined)[]): ActivityRateModelOption[] => {
  const byName = new Map<string, ActivityRateModelOption>();
  for (const option of lists.flatMap((list) => list ?? [])) {
    const known = byName.get(option.name);
    byName.set(option.name, { name: option.name, hauling: option.hauling || !!known?.hauling });
  }
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * The rate models counted: those picked, else the hauling ones (as WaterGo's Driver Activity starts), else
 * every one when none of them hauls.
 */
export const countedRateModels = (picked: string[] | undefined, options: ActivityRateModelOption[]): string[] => {
  if (picked) return picked;
  const hauling = options.filter((option) => option.hauling).map((option) => option.name);
  return hauling.length ? hauling : options.map((option) => option.name);
};

/** Whether some rate model on offer is left out of the count. */
export const rateModelsNarrowed = (counted: string[], options: ActivityRateModelOption[]) =>
  options.some((option) => !counted.includes(option.name));
